import { CaretRightFilled } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  memo,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex } from '../base';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import FadeDiv from '../Common/FadeDiv';
import Spinner from '../Common/Spinner';
import List from '../List/List';
import Typo from '../Typography/Typo';
import treeSelectUtils from './treeSelectUtils';
import type { TreeSelectItemBase } from './types';

type Props<T extends {}> = {
  mode?: 'single' | 'multiple';
  /**
   * @name isCascading
   * @description
   * 부모가 선택되었을때 자식들도 함께 선택된 것으로 표시되는 모드 (실제 선택된 것은 부모만 선택됨)
   * 부모가 선택된 상태에서 자식들 중 하나를 uncheck하면 부모 선택이 풀리면서 나머지 자식들이 전부 선택됨
   **/
  isCascading?: boolean;
  dataList: TreeSelectItemBase<T>[];
  updateParentChildNode?: (
    parentAlias: StringNumberValue,
    items: TreeSelectItemBase<T>[],
  ) => void;
  selectedList: StringNumberValue[];
  paginatedOptions?: {
    hasNextPage: boolean;
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
  };
  onCheck: (props: {
    value: StringNumberValue;
    exceptSelf?: boolean;
    exceptChild?: boolean;
    action?: 'add' | 'remove';
    meta?: T;
    parentAlias?: StringNumberValue;
    parentSelected?: boolean;
  }) => void;
  loading?: boolean;
};

const Tree = <T extends {}>(props: Props<T>) => {
  const isSingleMode = props.mode === 'single';

  // selectedList를 Set으로 변환하여 O(1) 조회 성능 개선
  const selectedSet = useMemo(
    () => new Set(props.selectedList),
    [props.selectedList],
  );

  return (
    <div className="py-1 px-2 flex h-full min-h-0 flex-col w-full">
      <List
        className="min-h-0 overflow-unset"
        data={props.dataList}
        isLoading={props.loading}
        hasNextPage={props.paginatedOptions?.hasNextPage}
        fetchNextPage={props.paginatedOptions?.fetchNextPage}
        isFetchingNextPage={props.paginatedOptions?.isFetchingNextPage}
        loader={
          <FadeDiv status={true}>
            <CenterFlex style={{ height: 140 }}>
              <Spinner />
            </CenterFlex>
          </FadeDiv>
        }
        placeholder={{
          node: (
            <Flex style={{ height: 80 }} a="center" j="center">
              <EmptyMessage>No contents</EmptyMessage>
            </Flex>
          ),
        }}
        render={(option, index) => {
          return (
            <TreeItem
              isCascading={props.isCascading}
              isSingleMode={isSingleMode}
              key={`${option.alias}-${index}`}
              {...option}
              onCheck={props.onCheck}
              selectedSet={selectedSet}
              updateParentChildNode={props.updateParentChildNode}
              indent={0}
            />
          );
        }}
      />
    </div>
  );
};

const EmptyMessage = styled(Typo).attrs({
  type: 'label3',
})`
  color: ${(props) => props.theme.colors.gray500};
`;

type TreeItemProps<T extends {}> = TreeSelectItemBase<T> & {
  isCascading?: boolean;
  indent: number;
  isSingleMode: boolean;
  parentAlias?: StringNumberValue;
  siblings?: TreeSelectItemBase<T>[];
  parentSelected?: boolean;
  onCheck: (props: {
    value: StringNumberValue;
    exceptSelf?: boolean;
    exceptChild?: boolean;
    action?: 'add' | 'remove';
    meta?: T;
    parentAlias?: StringNumberValue;
    parentSelected?: boolean;
  }) => void;
  selectedSet: Set<StringNumberValue>;
  updateParentChildNode?: (
    parentAlias: StringNumberValue,
    items: TreeSelectItemBase<T>[],
  ) => void;
};

const TreeItemComponent = <T extends {}>(props: TreeItemProps<T>) => {
  const [collapsed, setCollapsed] = useState(true);

  // props destructure for better memoization
  const {
    alias,
    asyncGetChildNodes,
    childNodes,
    checkOption,
    isCascading,
    isSingleMode,
    meta,
    node,
    parentAlias,
    parentSelected,
    selectedSet,
    onCheck,
    updateParentChildNode,
    indent,
  } = props;

  const { data, isLoading, refetch } = useQuery({
    queryFn: () => asyncGetChildNodes?.(alias),
    enabled: false,
    queryKey: ['tree-item-child', alias],
  });

  useEffect(() => {
    if (data) {
      updateParentChildNode?.(alias, data || []);
    }
  }, [data, alias, updateParentChildNode]);

  const childs = useMemo(() => {
    if (asyncGetChildNodes) return data || [];
    return childNodes || [];
  }, [data, asyncGetChildNodes, childNodes]);

  const hasChild = useMemo(() => {
    if (asyncGetChildNodes) return true;
    return childNodes ? childNodes?.length > 0 : false;
  }, [asyncGetChildNodes, childNodes]);

  // indent 배열을 메모이제이션
  const indentElements = useMemo(
    () =>
      Array(indent)
        .fill(null)
        .map((_, index) => <IndentBox key={index} />),
    [indent],
  );

  const indentElementsForButtons = useMemo(
    () =>
      Array(indent + 1)
        .fill(null)
        .map((_, index) => <IndentBox key={index} />),
    [indent],
  );

  // checked 상태 계산: cascading 모드일 때는 부모 선택 또는 모든 자식 선택 시 true
  const checked = useMemo(() => {
    const isSelfSelected = selectedSet.has(alias);

    if (!isCascading) {
      return isSelfSelected;
    }

    // cascading 모드일 때
    if (isSelfSelected) {
      return true; // 부모가 직접 선택된 경우
    }

    // 부모가 선택된 경우 선택된 것으로 표시
    if (parentSelected) return true;

    return false;
  }, [selectedSet, alias, isCascading, parentSelected]);

  // indeterminate 상태 계산: cascading 모드일 때 일부 자식만 선택된 경우
  const indeterminate = useMemo(() => {
    if (!isCascading) return false;
    if (!hasChild) return false;
    if (checked) return false;

    const isSelfSelected = selectedSet.has(alias);
    // 부모가 선택된 경우는 indeterminate가 아님
    if (isSelfSelected) return false;

    // 일부 자식만 선택된 경우
    // selectedSet을 배열로 변환 (treeSelectUtils가 배열을 기대함)
    return treeSelectUtils.areSomeChildrenSelected(
      childs,
      Array.from(selectedSet),
    );
  }, [isCascading, selectedSet, alias, hasChild, checked, childs]);

  const onCaretClick = useCallback(
    (e: MouseEvent) => {
      if (!hasChild) return;
      e.stopPropagation();
      if (asyncGetChildNodes) {
        refetch();
      }
      setCollapsed((prev) => !prev);
    },
    [hasChild, asyncGetChildNodes, refetch],
  );

  const onItemClick = useCallback(async () => {
    if (asyncGetChildNodes) {
      refetch();
    }
    if (checkOption?.disable) return;
    onCheck({
      value: alias,
      exceptChild:
        typeof checkOption?.exceptChild === 'undefined'
          ? true
          : checkOption.exceptChild,
      exceptSelf: checkOption?.exceptSelf,
      meta: meta,
      parentAlias: parentAlias,
      parentSelected: parentSelected,
    });
  }, [
    asyncGetChildNodes,
    checkOption?.disable,
    checkOption?.exceptChild,
    checkOption?.exceptSelf,
    onCheck,
    alias,
    meta,
    parentAlias,
    parentSelected,
    refetch,
  ]);

  const onCheckHandler = useCallback(
    (_: boolean, e?: MouseEvent) => {
      e?.stopPropagation();
      onCheck({
        value: alias,
        exceptChild:
          typeof checkOption?.exceptChild === 'undefined'
            ? true
            : checkOption.exceptChild,
        exceptSelf: checkOption?.exceptSelf,
        meta: meta,
        parentAlias: parentAlias,
        parentSelected: parentSelected,
      });
    },
    [onCheck, alias, checkOption, meta, parentAlias, parentSelected],
  );

  const onSelectAllClick = useCallback(() => {
    onCheck({
      value: alias,
      exceptSelf: true,
      action: 'add',
      meta: meta,
      parentAlias: parentAlias,
      parentSelected: parentSelected,
    });
  }, [onCheck, alias, meta, parentAlias, parentSelected]);

  const onDeelectAllClick = useCallback(() => {
    onCheck({
      value: alias,
      exceptSelf: true,
      action: 'remove',
      meta: meta,
      parentAlias: parentAlias,
      parentSelected: parentSelected,
    });
  }, [onCheck, alias, meta, parentAlias, parentSelected]);

  return (
    <>
      <TreeItemStyle g={2} a="center" onClick={onItemClick}>
        <Flex onClick={onCaretClick}>{indentElements}</Flex>
        <Flex a="center">
          <CenterFlex style={{ width: 12, height: 12 }}>
            {hasChild && (
              <ButtonWrapper onClick={onCaretClick}>
                <CaretRightFilled
                  style={{
                    fontSize: 12,
                    transform: collapsed ? 'none' : 'rotate(90deg)',
                    transition: 'all 0.2s',
                  }}
                />
              </ButtonWrapper>
            )}
          </CenterFlex>
          {!checkOption?.disable && !isSingleMode && (
            <Checkbox
              checked={checked}
              halfCheck={indeterminate}
              onChange={onCheckHandler}
            />
          )}
        </Flex>
        <ItemWrapper>{node}</ItemWrapper>
      </TreeItemStyle>
      {!collapsed && (
        <>
          {!isSingleMode && !isCascading && (
            <Flex style={{ paddingLeft: 12 }}>
              <Flex>{indentElementsForButtons}</Flex>
              <Button variant="ghost" sizeType="s" onClick={onSelectAllClick}>
                Select all
              </Button>
              <Button variant="ghost" sizeType="s" onClick={onDeelectAllClick}>
                Deselect all
              </Button>
            </Flex>
          )}
          <List
            data={childs}
            isLoading={isLoading}
            className="min-h-fit"
            render={(childNode) => (
              <TreeItem
                isCascading={isCascading}
                isSingleMode={isSingleMode}
                key={childNode.alias}
                {...childNode}
                parentAlias={alias}
                siblings={childs}
                onCheck={onCheck}
                selectedSet={selectedSet}
                indent={indent + 1}
                updateParentChildNode={updateParentChildNode}
                parentSelected={checked}
              />
            )}
          />
        </>
      )}
    </>
  );
};

// React.memo로 TreeItem 최적화 - props가 변경되지 않으면 리렌더링 방지
const TreeItem = memo(TreeItemComponent) as typeof TreeItemComponent;

const TreeItemStyle = styled(Flex)`
  cursor: pointer;
  width: 100%;
  padding: 4px 0px;
  transition: all 0.2s;
  border-radius: 8px;
  content-visibility: auto;
  contain-intrinsic-size: 100% 32px;
  &:hover {
    background: ${(props) => props.theme.colors.ND04};
  }
`;

const ButtonWrapper = styled(CenterFlex)`
  cursor: pointer;
  width: 20px;
  height: 20px;
`;

const IndentBox = styled(Flex)`
  width: 12px;
  height: 16px;
`;

export const ItemWrapper = styled(Flex)`
  align-items: center;
  min-height: 24px;
  padding: 4px;
  border-radius: 4px;
  font-size: 12px;

  color: ${(props) => props.theme.colors.gray700};
`;

export default Tree;
